import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { TID } from "@/constants/testIds";

const LINKS = [
    { to: "/", label: "Home", testid: TID.nav.home },
    { to: "/journey", label: "My Journey", testid: TID.nav.journey },
    {
        to: "/building",
        label: "What I'm Building",
        testid: TID.nav.building,
        sub: [
            { to: "/building/valanti-labs", label: "Valanti Labs", testid: TID.nav.labs },
            { to: "/building/alati", label: "ALATI", testid: TID.nav.alati },
            { to: "/building/music", label: "Music", testid: TID.nav.music },
        ],
    },
    // { to: "/journal", label: "Journal", testid: TID.nav.journal },  // Temporarily hidden — Journal pages, articles, routes and DB records remain intact for later restoration.
    { to: "/media", label: "Media & Speaking", testid: TID.nav.media },
    { to: "/contact", label: "Contact", testid: TID.nav.contact },
];

// Routes whose top-of-page hero is on a charcoal background. When the header
// is transparent over these routes, nav text needs to flip to ivory/gold for
// contrast. Other routes (Privacy, Terms, Journal, Article, Contact) have an
// ivory hero — the existing charcoal/oxblood scheme is correct for them.
const DARK_HERO_ROUTES = ["/", "/journey", "/building", "/media"];

function isDarkHeroPath(pathname) {
    return DARK_HERO_ROUTES.some(
        (p) => pathname === p || pathname.startsWith(p + "/")
    );
}

export default function Navigation() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [subOpen, setSubOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setSubOpen(false);
    }, [location.pathname]);

    // When the header sits transparent over a dark hero, switch nav text
    // colours to ivory (inactive) + gold (active) for legibility.
    const darkHeader = !scrolled && !mobileOpen && isDarkHeroPath(location.pathname);

    const brandColor = darkHeader ? "text-ivory" : "text-charcoal";
    const mobileBtnColor = darkHeader ? "text-ivory" : "text-charcoal";

    const inactiveClass = darkHeader
        ? "text-ivory/85 hover:text-gold"
        : "text-charcoal hover:text-oxblood";
    const activeClass = darkHeader ? "text-gold" : "text-oxblood";

    return (
        <header
            data-testid={TID.nav.wrapper}
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled || mobileOpen
                    ? "bg-ivory/95 backdrop-blur-md border-b border-charcoal/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-editorial flex items-center justify-between h-20 md:h-24">
                <Link
                    to="/"
                    data-testid={TID.nav.brand}
                    className={`font-serif text-2xl md:text-[26px] tracking-tight leading-none transition-colors ${brandColor}`}
                >
                    Chrissa Valanti
                </Link>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-10">
                    {LINKS.map((l) =>
                        l.sub ? (
                            <div
                                key={l.to}
                                className="relative"
                                onMouseEnter={() => setSubOpen(true)}
                                onMouseLeave={() => setSubOpen(false)}
                            >
                                <NavLink
                                    to={l.to}
                                    data-testid={l.testid}
                                    className={({ isActive }) =>
                                        `eyebrow inline-flex items-center gap-1.5 transition-colors ${
                                            isActive ? activeClass : inactiveClass
                                        }`
                                    }
                                >
                                    {l.label}
                                    <ChevronDown className="w-3 h-3 mt-0.5" strokeWidth={1.5} />
                                </NavLink>
                                {subOpen && (
                                    <div className="absolute left-0 top-full pt-4">
                                        <div className="bg-ivory border border-charcoal/10 py-3 min-w-[200px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.18)]">
                                            {l.sub.map((s) => (
                                                <NavLink
                                                    key={s.to}
                                                    to={s.to}
                                                    data-testid={s.testid}
                                                    className="block px-5 py-2 text-sm font-light text-charcoal hover:bg-ivory-soft hover:text-oxblood transition-colors"
                                                >
                                                    {s.label}
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={l.testid}
                                className={({ isActive }) =>
                                    `eyebrow transition-colors ${
                                        isActive ? activeClass : inactiveClass
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        )
                    )}
                </nav>

                <button
                    type="button"
                    aria-label="Toggle menu"
                    data-testid={TID.nav.mobileToggle}
                    className={`lg:hidden p-2 -mr-2 transition-colors ${mobileBtnColor}`}
                    onClick={() => setMobileOpen((v) => !v)}
                >
                    {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
                </button>
            </div>

            {/* Mobile nav */}
            {mobileOpen && (
                <div
                    data-testid={TID.nav.mobileMenu}
                    className="lg:hidden border-t border-charcoal/10 bg-ivory"
                >
                    <nav className="container-editorial py-6 flex flex-col gap-1">
                        {LINKS.map((l) => (
                            <div key={l.to}>
                                <NavLink
                                    to={l.to}
                                    data-testid={l.testid + "-mobile"}
                                    className={({ isActive }) =>
                                        `block py-3 font-serif text-2xl ${isActive ? "text-oxblood" : "text-charcoal"}`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                                {l.sub && (
                                    <div className="pl-4 pb-2 flex flex-col">
                                        {l.sub.map((s) => (
                                            <NavLink
                                                key={s.to}
                                                to={s.to}
                                                data-testid={s.testid + "-mobile"}
                                                className="py-2 text-sm font-light text-charcoal/70 hover:text-oxblood"
                                            >
                                                — {s.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </nav>
                </div>
            )}
        </header>
    );
}
